import {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useMemo,
  useState,
} from 'react';
import createSafeContext from '@paradigm/utils/createSafeContext';

interface State {
  readonly selectedRows: Record<number, boolean>;
}

interface SelectedPositionRowsView {
  /** this is created to match react-table controlled state */
  readonly selectedRows: State['selectedRows'];
  readonly isEmptySelection: boolean;
}

interface SelectedPositionRowsActions {
  readonly setSelectedRows: Dispatch<
    SetStateAction<SelectedPositionRowsView['selectedRows']>
  >;
  readonly clearSelectedRows: () => void;
}

const [useSelectedPositionRowsView, SelectedPositionRowsViewProvider] =
  createSafeContext<SelectedPositionRowsView>('SelectedPositionRowsView');
const [useSelectedPositionRowsActions, SelectedPositionRowsActionsProvider] =
  createSafeContext<SelectedPositionRowsActions>('SelectedPositionRowsActions');

export { useSelectedPositionRowsView, useSelectedPositionRowsActions };

export function SelectedPositionRowsProvider({ children }: PropsWithChildren) {
  const [selectedRows, setSelectedRows] = useState<State['selectedRows']>({});

  const view: SelectedPositionRowsView = useMemo(
    () => createView({ selectedRows }),
    [selectedRows],
  );

  const actions: SelectedPositionRowsActions = useMemo(
    () => ({
      setSelectedRows,
      clearSelectedRows: () => setSelectedRows({}),
    }),
    [],
  );

  return (
    <SelectedPositionRowsViewProvider value={view}>
      <SelectedPositionRowsActionsProvider value={actions}>
        {children}
      </SelectedPositionRowsActionsProvider>
    </SelectedPositionRowsViewProvider>
  );
}

function createView(state: State): SelectedPositionRowsView {
  return {
    isEmptySelection: Object.keys(state.selectedRows).length === 0,
    selectedRows: state.selectedRows,
  };
}
